import { Component, OnInit, HostBinding, OnDestroy, ChangeDetectorRef } from "@angular/core";
import { BehaviorSubject, Subscription, combineLatest } from "rxjs";
import { AdvQueueService } from "@onsip/common/services/api/resources/queue/adv-queue.service";
import { AdvQueue } from "@onsip/common/services/api/resources/queue/adv-queue";
import { MatDialog } from "@angular/material/dialog";
import { TranslateService } from "@ngx-translate/core";
import { filter, map } from "rxjs/operators";
import { SnackbarService } from "@onsip/web/features/shared/components/snackbar/snackbar.service";
import { AccountResourceService } from "@onsip/common/services/api/resources/package/account-resource.service";
import { PackageService } from "@onsip/common/services/api/resources/package/package.service";
import { BreakpointObserver } from "@angular/cdk/layout";
import { UserAliasService } from "@onsip/common/services/api/resources/userAlias/user-alias.service";
import { AppAddressService } from "@onsip/common/services/api/resources/appAddress/app-address.service";
import { AdvQueueEmailReportService } from "@onsip/common/services/api/resources/queue/adv-queue-email-report.service";
import { AdminTableAbstractComponent } from "@onsip/web/features/abstractClass/admin-table-abstract.component";
import { E2eLocators } from "@onsip/e2e/configs/locators";
import { FormatExtensionsPipe } from "@onsip/web/features/shared/pipes/format-extensions.pipe";
import { AddressSummaryService } from "@onsip/common/services/api/resources/addressSummary/address-summary.service";

export interface QueueWithExt extends AdvQueue {
  queueExtensions: Array<string>;
  loginExtensions: Array<string>;
  loginSipAddress: string;
}

@Component({
  selector: "onsip-acd-queues",
  templateUrl: "./acd-queues-page.component.html",
  styleUrls: ["./acd-queues-page.component.scss"],
  providers: [FormatExtensionsPipe],
  standalone: false
})
export class AcdQueuesPageComponent
  extends AdminTableAbstractComponent<QueueWithExt>
  implements OnInit, OnDestroy
{
  @HostBinding("class.onsip-grid-content")
  @HostBinding("class.onsip-admin-host-grid")
  _dontUse = true;
  E2eLocators = E2eLocators;

  hasQueues = new BehaviorSubject<boolean>(true);
  queuePricing!: String;
  userUnlimited = false;
  emptySimplePricingText!: String;
  emptyEnhancedPricingText!: String;
  isDataObtained$ = new BehaviorSubject(false);

  protected displayedColumnsRecord = {
    XSmall: ["name", "actions"],
    Small: ["name", "numberOfAgents", "actions"],
    Medium: ["name", "numberOfAgents", "extensions", "actions"],
    Large: ["name", "numberOfAgents", "extensions", "actions"],
    XLarge: ["name", "numberOfAgents", "extensions", "actions"]
  };
  protected unsubscriber = new Subscription();

  constructor(
    private queueService: AdvQueueService,
    private snackbar: SnackbarService,
    private resourceService: AccountResourceService,
    private packageService: PackageService,
    private userAlias: UserAliasService,
    private appAddress: AppAddressService,
    private emailReportService: AdvQueueEmailReportService,
    private formatExtensions: FormatExtensionsPipe,
    private addressSummaryService: AddressSummaryService,
    protected translate: TranslateService,
    protected breakpointObserver: BreakpointObserver,
    protected dialog: MatDialog,
    protected cdr: ChangeDetectorRef
  ) {
    super(breakpointObserver, cdr, dialog, translate);
  }

  ngOnInit(): void {
    super.ngOnInit();
    this.setDataFilter();

    this.initUserUnlimited();
    this.getPricing();
  }

  ngOnDestroy() {
    super.ngOnDestroy();
    this.unsubscriber.unsubscribe();
  }

  trackByQueueId(index: number, item: QueueWithExt): string {
    return item.advQueueId;
  }

  getPricingText(): string {
    return `Pricing: $${this.emptySimplePricingText}/month per agent, or enable enhanced settings for $${this.emptyEnhancedPricingText}/month.`;
  }

  protected getItemValueByColumn(item: QueueWithExt, columnName: string): string | number {
    switch (columnName) {
      case "name":
        return item.queueName.trim().toLowerCase();
      case "numberOfAgents":
        return parseInt(item.maxAgent);
      case "extensions":
        return item.queueExtensions.join("");
      default:
        return parseInt(item.advQueueId);
    }
  }

  protected performDeleteRequest(queue: QueueWithExt): void {
    this.queueService.advQueueDelete(queue.advQueueId).then(() => {
      this.getQueues();
      this.snackbar.openSnackBar(
        this.translate.instant("ONSIP_I18N.VALUE_HAS_BEEN_DELETED", { value: "Queue" }),
        "success"
      );
      this.cdr.markForCheck();
    });
  }

  protected getItemName({ queueName }: QueueWithExt): string {
    return queueName;
  }

  protected initDataSource(): void {
    this.userAlias.userAliasBrowse();
    this.addressSummaryService.addressSummaryBrowse();
    this.emailReportService.advQueueEmailReportBrowse();
    this.getQueues();
  }

  protected setDataFilter(): void {
    this.dataSource.filterPredicate = (data, filter) => {
      const filterValue = filter.trim().toLowerCase();
      // name
      const name = data.queueName;
      // number of agents
      const agents = data.maxAgent || "0";
      // ext
      const ext = this.formatExtensions.transform(data.queueExtensions).join(", ");

      return (
        name.toLowerCase().includes(filterValue) ||
        agents.toLowerCase().includes(filterValue) ||
        ext.toLowerCase().includes(filterValue)
      );
    };
  }

  private getQueues() {
    this.queueService.advQueueBrowse();
    this.appAddress.appAddressBrowse();
    this.unsubscriber.add(
      combineLatest({
        aliases: this.userAlias.orgState,
        queues: this.queueService.orgState,
        appAddresses: this.appAddress.orgState
      })
        .pipe(
          map(({ aliases, queues, appAddresses }) => {
            this.hasQueues.next(queues.length > 0);
            const queuesWithExt = queues.map(queue => ({
              ...queue,
              queueExtensions: aliases
                .filter(
                  alias =>
                    alias.addressUsername ===
                    queue.appAddress.substring(0, queue.appAddress.indexOf("@"))
                )
                .map(alias => alias.aliasUsername),
              loginExtensions: aliases
                .filter(
                  alias =>
                    alias.addressUsername ===
                    appAddresses.find(appAdd => appAdd.appId === queue.advQueueLoginAppId)?.address
                      .username
                )
                .map(alias => alias.aliasUsername),
              loginSipAddress:
                appAddresses.find(appAdd => appAdd.appId === queue.advQueueLoginAppId)?.address
                  .username || ""
            }));
            return queuesWithExt;
          })
        )
        .subscribe(queuesWithExt => {
          this.dataSource.data = queuesWithExt;
          this.isDataObtained$.next(true);
        })
    );
  }

  private getPricing(): void {
    this.resourceService.accountResourceBrowse();
    this.unsubscriber.add(
      this.resourceService.state
        .pipe(
          filter(state => !state.loading),
          map(state => {
            return Object.values(state.state);
          })
        )
        .pipe(
          map(resourcesArray => {
            return resourcesArray.filter(
              res => res.name === "SimpleQueue" || res.name === "SmartQueue"
            );
          })
        )
        .subscribe(resources => {
          resources.map(res => {
            if (res.name === "SimpleQueue") {
              this.emptySimplePricingText = res.monthlyRecurringCharge;
            } else {
              this.emptyEnhancedPricingText = res.monthlyRecurringCharge;
            }
          });
          this.cdr.markForCheck();
        })
    );
  }

  private initUserUnlimited(): void {
    this.unsubscriber.add(
      this.packageService.getAssignedPrimaryPackages().subscribe(packages => {
        packages.map(pkg => {
          if (pkg.status === "user") {
            this.userUnlimited = true;
          }
        });
        this.cdr.markForCheck();
      })
    );
  }
}
